import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  Chip,
} from '@mui/material';
import {
  AccountBalance as LoansIcon,
  Home as PropertiesIcon,
  Business as CompaniesIcon,
  AttachMoney as IncomeIcon,
  Person as PersonIcon,
  Group as FamilyIcon,
  AccountBalanceWallet as AccountIcon,
  Refresh as RefreshIcon,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

interface Entity {
  id: number;
  name?: string;
  loan_name?: string;
  address?: string;
  nickname?: string;
  company_name?: string;
  description?: string;
  family_id?: number | null;
  family_name?: string | null;
  [key: string]: any;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  family_id?: number | null;
  family_name?: string | null;
}

interface EntityPermission {
  entity: Entity;
  user_permissions: {
    [userId: number]: {
      user: User;
      can_view: boolean;
      can_edit: boolean;
      can_delete: boolean;
      has_permission: boolean;
    };
  };
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const ENTITY_CATEGORIES = [
  { value: 'property', label: 'Properties', icon: <PropertiesIcon /> },
  { value: 'loan', label: 'Loans', icon: <LoansIcon /> },
  { value: 'company', label: 'Companies', icon: <CompaniesIcon /> },
  { value: 'income', label: 'Incomes', icon: <IncomeIcon /> },
  { value: 'person', label: 'People', icon: <PersonIcon /> },
  { value: 'family', label: 'Families', icon: <FamilyIcon /> },
  { value: 'account', label: 'Accounts', icon: <AccountIcon /> },
];

const ENTITY_TABS = [
  { value: 'all', label: 'All', icon: <DashboardIcon /> },
  ...ENTITY_CATEGORIES
];

export const EntityPermissions: React.FC = () => {
  const [categoryTab, setCategoryTab] = useState(0); // Default to ALL tab (index 0)
  const [entitiesByType, setEntitiesByType] = useState<Record<string, EntityPermission[]>>({});
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState<Record<string, boolean>>({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentEntityType, setCurrentEntityType] = useState('property');
  const [hasLoadedUsers, setHasLoadedUsers] = useState(false);
  const fetchEntityPermissions = async (entityType: string, options: { showGlobalLoading?: boolean } = {}) => {
    const { showGlobalLoading = true } = options;
    if (showGlobalLoading) {
      setLoading(true);
    }
    setLoadingTypes(prev => ({ ...prev, [entityType]: true }));
    try {
      setError('');
      const response = await api.get(`/api/admin/entity-permissions/${entityType}`);
      const newEntities: EntityPermission[] = response.data.entities || [];
      setEntitiesByType(prev => ({
        ...prev,
        [entityType]: newEntities
      }));
      const fetchedUsers: User[] = response.data.users || [];
      if (!hasLoadedUsers && fetchedUsers.length > 0) {
        setUsers(fetchedUsers);
        setHasLoadedUsers(true);
      } else if (fetchedUsers.length > 0) {
        setUsers(fetchedUsers);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch entity permissions');
      console.error('Error fetching entity permissions:', err);
    } finally {
      setLoadingTypes(prev => ({ ...prev, [entityType]: false }));
      if (showGlobalLoading) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const selectedTab = ENTITY_TABS[categoryTab];
    if (!selectedTab) {
      return;
    }

    if (selectedTab.value === 'all') {
      const typesToFetch = ENTITY_CATEGORIES
        .filter(cat => !entitiesByType[cat.value] || entitiesByType[cat.value]?.length === 0);
      if (typesToFetch.length === 0) {
        return;
      }
      setLoading(true);
      Promise.all(
        typesToFetch.map(cat => fetchEntityPermissions(cat.value, { showGlobalLoading: false }))
      ).finally(() => setLoading(false));
    } else {
      setCurrentEntityType(selectedTab.value);
      if (!entitiesByType[selectedTab.value]) {
        fetchEntityPermissions(selectedTab.value);
      }
    }
  }, [categoryTab]);

  const handlePermissionChange = async (
    entityType: string,
    entityId: number,
    userId: number,
    value: boolean
  ) => {
    try {
      await api.put(`/api/admin/entity-permissions/${entityType}/${entityId}/user/${userId}`, {
        can_view: value,
        can_edit: false,
        can_delete: false
      });

      // Update local state
      setEntitiesByType(prev => {
        const currentList = prev[entityType] || [];
        const updatedList = currentList.map(e => {
          if (e.entity.id === entityId) {
            return {
              ...e,
              user_permissions: {
                ...e.user_permissions,
                [userId]: {
                  ...e.user_permissions[userId],
                  can_view: value,
                  has_permission: value
                }
              }
            };
          }
          return e;
        });
        return {
          ...prev,
          [entityType]: updatedList
        };
      });

      setSuccess('Permission updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update permission');
      console.error('Error updating permission:', err);
    }
  };

  const getEntityName = (entity: Entity, contextType: string = currentEntityType): string => {
    // Loans
    if (entity.loan_name) return entity.loan_name;
    
    // Properties
    if (entity.address) {
      if (entity.nickname) return `${entity.nickname} (${entity.address})`;
      return entity.address;
    }
    
    // Companies
    if (entity.company_name || entity.name) return entity.company_name || entity.name || '';
    
    // Income - show meaningful name
    if (entity.income_name) {
      return entity.income_name;
    }
    if (entity.source || entity.income_type) {
      const parts: string[] = [];
      if (entity.income_type) parts.push(entity.income_type);
      if (entity.source) parts.push(entity.source);
      if (entity.amount_yearly) {
        parts.push(`€${entity.amount_yearly.toLocaleString()}/yr`);
      } else if (entity.amount_monthly) {
        parts.push(`€${entity.amount_monthly.toLocaleString()}/mo`);
      }
      if (entity.person_name) parts.push(`(${entity.person_name})`);
      return parts.join(' - ') || `Income #${entity.id}`;
    }
    
    // People
    if (entity.name && entity.relationship) {
      return `${entity.name} (${entity.relationship})`;
    }
    if (entity.name) return entity.name;
    
    // Families
    if (entity.name && contextType === 'family') {
      return entity.name;
    }
    
    // Accounts
    if (entity.account_name) return entity.account_name;
    if (entity.account_type) return `${entity.account_type} Account`;
    
    // Generic fallback
    if (entity.description) return entity.description;
    return `Entity #${entity.id}`;
  };

  // Group entities by family
  const groupEntitiesByFamily = (entities: EntityPermission[]) => {
    const grouped: { [familyKey: string]: EntityPermission[] } = {
      'no-family': []
    };
    
    entities.forEach(entityPerm => {
      const familyKey = entityPerm.entity.family_id 
        ? `family-${entityPerm.entity.family_id}` 
        : 'no-family';
      
      if (!grouped[familyKey]) {
        grouped[familyKey] = [];
      }
      grouped[familyKey].push(entityPerm);
    });
    
    return grouped;
  };

  // Group users by family (prioritize family grouping over role)
  const groupUsersByFamily = (users: User[]) => {
    const grouped: { [familyKey: string]: User[] } = {
      'no-family': []
    };
    
    // Separate admin users (non-owner, no family) into its own group
    const adminUsers: User[] = [];
    
    users.forEach(user => {
      if (user.family_id) {
        // Group by family (includes owner users like Omar with their family)
        const familyKey = `family-${user.family_id}`;
        if (!grouped[familyKey]) {
          grouped[familyKey] = [];
        }
        grouped[familyKey].push(user);
      } else if (user.role === 'admin') {
        // Admin users without a family go to separate admin group
        adminUsers.push(user);
      } else {
        // Users without family (non-admin)
        grouped['no-family'].push(user);
      }
    });
    
    // Add admin users as a separate group if any exist
    if (adminUsers.length > 0) {
      grouped['admin'] = adminUsers;
    }
    
    return grouped;
  };

  const userGroups = useMemo(() => groupUsersByFamily(users), [users]);

  const userColumnKeys = useMemo(
    () =>
      Object.entries(userGroups).flatMap(([familyKey, familyUsers]) =>
        familyUsers.map(user => `${familyKey}-${user.id}`)
      ),
    [userGroups]
  );

  const renderEntityTable = (
    entityType: string,
    label: string,
    entityPermissions: EntityPermission[] | undefined,
    isLoadingType: boolean
  ) => {
    const entities = entityPermissions || [];
    const entityGroups = groupEntitiesByFamily(entities);
    const sortedEntities = [...entities].sort((a, b) => {
      const aFamily = a.entity.family_name || 'ZZZ';
      const bFamily = b.entity.family_name || 'ZZZ';
      if (aFamily !== bFamily) {
        return aFamily.localeCompare(bFamily);
      }
      return getEntityName(a.entity, entityType).localeCompare(getEntityName(b.entity, entityType));
    });

    return (
      <Card key={entityType} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {label} Permissions
          </Typography>
          
          {isLoadingType ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : entities.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              No {label.toLowerCase()} found
            </Typography>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ ...FIT_TABLE_CONTAINER_SX, maxHeight: '70vh', overflowY: 'auto' }}
            >
              <Table size="small" stickyHeader sx={FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'entity', pct: 22 },
                    ...userColumnKeys.map(key => ({
                      key,
                      pct: Number((78 / Math.max(1, userColumnKeys.length)).toFixed(3)),
                    })),
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell
                      className="fit-table-no-clip"
                      sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 10 }}
                    >
                      <strong>Entity</strong>
                    </TableCell>
                    {/* User columns grouped by family */}
                    {Object.entries(userGroups).map(([familyKey, familyUsers]) => {
                      if (familyUsers.length === 0) return null;
                      
                      const familyName = familyKey === 'admin' 
                        ? 'Admin' 
                        : familyKey === 'no-family'
                        ? 'No Family'
                        : familyUsers[0].family_name || `Family ${familyUsers[0].family_id}`;
                      
                      return (
                        <React.Fragment key={familyKey}>
                          <TableCell 
                            colSpan={familyUsers.length} 
                            align="center" 
                            sx={{ 
                              backgroundColor: familyKey === 'admin' ? '#f5f5f5' : '#e3f2fd',
                              fontWeight: 'bold',
                              borderLeft: '1px solid #e0e0e0'
                            }}
                          >
                            <Box component="span" sx={fitTruncateSx} title={familyName}>
                              {familyName}
                            </Box>
                          </TableCell>
                        </React.Fragment>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ position: 'sticky', left: 0, backgroundColor: 'white', zIndex: 10 }}>
                      {/* Empty cell for entity name column */}
                    </TableCell>
                    {Object.entries(userGroups).map(([familyKey, familyUsers]) => (
                      familyUsers.map(user => (
                        <TableCell 
                          key={`${familyKey}-${user.id}`} 
                          align="center"
                          sx={{ 
                            
                            backgroundColor: familyKey === 'admin' ? '#fafafa' : '#f5f9ff',
                            borderLeft: '1px solid #e0e0e0'
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="caption"
                              display="block"
                              fontWeight="bold"
                              sx={fitTruncateSx}
                              title={`${user.first_name} ${user.last_name}`}
                            >
                              {user.first_name} {user.last_name}
                            </Typography>
                            {(user.role === 'admin' || user.role === 'owner') && (
                              <Chip 
                                label={user.role === 'owner' ? 'OWNER' : 'ADMIN'} 
                                size="small" 
                                color={user.role === 'owner' ? 'error' : 'default'}
                                sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                            {user.role !== 'admin' && user.role !== 'owner' && (
                              <Chip 
                                label={user.role.toUpperCase()} 
                                size="small" 
                                sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                      ))
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedEntities.map((entityPerm, index) => {
                    const prevEntity = index > 0 ? sortedEntities[index - 1] : null;
                    const currentFamily = entityPerm.entity.family_name || 'No Family';
                    const prevFamily = prevEntity?.entity.family_name || 'No Family';
                    const showFamilyHeader = currentFamily !== prevFamily;
                    
                    return (
                      <React.Fragment key={`${entityType}-${entityPerm.entity.id}`}>
                        {/* Family header row */}
                        {showFamilyHeader && (
                          <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
                            <TableCell 
                              colSpan={users.length + 1}
                              sx={{ 
                                position: 'sticky', 
                                left: 0, 
                                backgroundColor: '#f0f0f0',
                                fontWeight: 'bold',
                                py: 1
                              }}
                            >
                              <Box component="span" sx={fitTruncateSx} title={currentFamily}>
                                {currentFamily}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                        
                        {/* Entity row */}
                        <TableRow>
                          <TableCell sx={{  pl: showFamilyHeader ? 4 : 2 }}>
                            <Typography
                              variant="body2"
                              sx={fitTruncateSx}
                              title={getEntityName(entityPerm.entity, entityType)}
                            >
                              {getEntityName(entityPerm.entity, entityType)}
                            </Typography>
                          </TableCell>
                          {Object.entries(userGroups).map(([userFamilyKey, familyUsers]) => (
                            familyUsers.map(user => {
                              const perm = entityPerm.user_permissions[user.id] || {
                                can_view: false,
                                can_edit: false,
                                can_delete: false,
                                has_permission: false
                              };
                              
                              // Owner always have access (read-only in UI), Admin users can be managed
                              const isOwner = user.role === 'owner';
                              
                              return (
                                <TableCell 
                                  key={`${entityType}-${entityPerm.entity.id}-${user.id}`} 
                                  align="center"
                                  sx={{ borderLeft: '1px solid #e0e0e0' }}
                                >
                                  <Switch
                                    size="small"
                                    checked={isOwner ? true : perm.can_view}
                                    disabled={isOwner}
                                    onChange={(e) => {
                                      if (!isOwner) {
                                        handlePermissionChange(
                                          entityType,
                                          entityPerm.entity.id,
                                          user.id,
                                          e.target.checked
                                        );
                                      }
                                    }}
                                  />
                                </TableCell>
                              );
                            })
                          ))}
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    );
  };


  const allTabContent = (
    <Box>
      {ENTITY_CATEGORIES.map(category => (
        <Box key={`all-section-${category.value}`} sx={{ mb: 4 }}>
          {renderEntityTable(
            category.value,
            category.label,
            entitiesByType[category.value],
            !!loadingTypes[category.value] && !entitiesByType[category.value]
          )}
        </Box>
      ))}
    </Box>
  );

  const selectedTab = ENTITY_TABS[categoryTab];
  const isAllTab = selectedTab?.value === 'all';

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Category Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs value={categoryTab} onChange={(e, newValue) => setCategoryTab(newValue)}>
            {ENTITY_TABS.map((cat, index) => (
              <Tab
                key={cat.value}
                icon={cat.icon}
                label={cat.label}
                iconPosition="start"
                {...({ 'aria-controls': `category-tabpanel-${index}` } as any)}
              />
            ))}
          </Tabs>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchEntityPermissions(currentEntityType)}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Category Tab Panels */}
      {ENTITY_TABS.map((cat, index) => (
        <TabPanel key={cat.value} value={categoryTab} index={index}>
          {cat.value === 'all' ? (
            loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              allTabContent
            )
          ) : loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            renderEntityTable(
              cat.value,
              cat.label,
              entitiesByType[cat.value],
              !!loadingTypes[cat.value] && !entitiesByType[cat.value]
            )
          )}
        </TabPanel>
      ))}
    </Box>
  );
};
